import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import { throwApiError } from "src/data/admin/apiError";
import {
  AdminRestaurantSubscriptionDto,
  GrantSubscriptionRequest,
  UpdateSubscriptionRequest,
} from "src/data/admin/types/subscription";

export const adminSubscriptionKeys = {
  list: () => ["admin", "subscriptions", "list"] as const,
  detail: (restaurantId: string) =>
    ["admin", "subscriptions", "detail", restaurantId] as const,
};

// GET /web/admin/subscriptions — Admin only
export const useAdminSubscriptions = () =>
  useQuery({
    queryKey: adminSubscriptionKeys.list(),
    queryFn: async (): Promise<AdminRestaurantSubscriptionDto[]> => {
      const response = await fetch(`${API_URL}/admin/subscriptions`, {
        credentials: "include",
      });
      if (!response.ok) {
        return throwApiError(response, "Failed to load subscriptions");
      }
      return (await response.json()) as AdminRestaurantSubscriptionDto[];
    },
  });

type MutationCallbacks = {
  onSuccess?: (subscription: AdminRestaurantSubscriptionDto) => void;
};

/**
 * Invalidates the ops list plus the restaurateur-facing subscription card, which
 * reads the same record through `/web/restaurants/{id}/subscription-tier`.
 */
const useSubscriptionInvalidation = () => {
  const queryClient = useQueryClient();

  return (restaurantId: string) => {
    queryClient.invalidateQueries({ queryKey: adminSubscriptionKeys.list() });
    queryClient.invalidateQueries({
      queryKey: adminSubscriptionKeys.detail(restaurantId),
    });
    queryClient.invalidateQueries({
      queryKey: ["subscription-tier", restaurantId],
    });
  };
};

// POST /web/admin/subscriptions/{restaurantId} — Admin only
// Creates the record, or overwrites tier / expiration / auto-renewal when one exists.
export const useGrantSubscription = ({ onSuccess }: MutationCallbacks = {}) => {
  const invalidate = useSubscriptionInvalidation();

  return useMutation({
    mutationFn: async ({
      restaurantId,
      payload,
    }: {
      restaurantId: string;
      payload: GrantSubscriptionRequest;
    }): Promise<AdminRestaurantSubscriptionDto> => {
      const response = await fetch(
        `${API_URL}/admin/subscriptions/${restaurantId}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) {
        return throwApiError(response, "Failed to grant the subscription");
      }
      return (await response.json()) as AdminRestaurantSubscriptionDto;
    },
    onSuccess: (subscription) => {
      invalidate(subscription.restaurantId);
      onSuccess?.(subscription);
    },
  });
};

// PUT /web/admin/subscriptions/{restaurantId} — Admin only
// Partial update: only the provided fields are applied.
export const useUpdateSubscription = ({ onSuccess }: MutationCallbacks = {}) => {
  const invalidate = useSubscriptionInvalidation();

  return useMutation({
    mutationFn: async ({
      restaurantId,
      payload,
    }: {
      restaurantId: string;
      payload: UpdateSubscriptionRequest;
    }): Promise<AdminRestaurantSubscriptionDto> => {
      const response = await fetch(
        `${API_URL}/admin/subscriptions/${restaurantId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) {
        return throwApiError(response, "Failed to update the subscription");
      }
      return (await response.json()) as AdminRestaurantSubscriptionDto;
    },
    onSuccess: (subscription) => {
      invalidate(subscription.restaurantId);
      onSuccess?.(subscription);
    },
  });
};

// POST /web/admin/subscriptions/{restaurantId}/renew?months=n — Admin only
// Extends from the current expiration, or from today once it has run out.
export const useRenewSubscription = ({ onSuccess }: MutationCallbacks = {}) => {
  const invalidate = useSubscriptionInvalidation();

  return useMutation({
    mutationFn: async ({
      restaurantId,
      months,
    }: {
      restaurantId: string;
      months: number;
    }): Promise<AdminRestaurantSubscriptionDto> => {
      const params = new URLSearchParams({ months: String(months) });
      const response = await fetch(
        `${API_URL}/admin/subscriptions/${restaurantId}/renew?${params}`,
        { method: "POST", credentials: "include" },
      );
      if (!response.ok) {
        return throwApiError(response, "Failed to renew the subscription");
      }
      return (await response.json()) as AdminRestaurantSubscriptionDto;
    },
    onSuccess: (subscription) => {
      invalidate(subscription.restaurantId);
      onSuccess?.(subscription);
    },
  });
};

// DELETE /web/admin/subscriptions/{restaurantId} — Admin only
// Revoke: deletes the record, cutting access immediately with no grace period.
// Returns 204; the organization link and staff assignments are left intact.
export const useRevokeSubscription = (onSuccess?: () => void) => {
  const invalidate = useSubscriptionInvalidation();

  return useMutation({
    mutationFn: async (restaurantId: string): Promise<void> => {
      const response = await fetch(
        `${API_URL}/admin/subscriptions/${restaurantId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok) {
        await throwApiError(response, "Failed to revoke the subscription");
      }
    },
    onSuccess: (_data, restaurantId) => {
      invalidate(restaurantId);
      onSuccess?.();
    },
  });
};
